import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

// Polls every 30s from any page — detects prop challenge pass/fail and shows a toast.
const POLL_MS = 30_000;

export function PropChallengeMonitor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Track the challenge ID we have already notified about to prevent duplicates.
  const notifiedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;

    async function checkChallenge() {
      if (cancelled) return;

      const { data: active } = await (supabase as any)
        .from("prop_challenges")
        .select("id, status, account_size, starting_balance, profit_target, daily_loss_limit, max_loss_limit, violations, started_at")
        .eq("user_id", user!.id)
        .eq("status", "active")
        .maybeSingle();

      if (!active || notifiedRef.current === active.id) return;

      const { data: trades } = await (supabase as any)
        .from("prop_challenge_trades")
        .select("pnl, status, closed_at, opened_at")
        .eq("challenge_id", active.id)
        .eq("status", "closed");

      // Include journal trades logged since the challenge started. While a challenge is
      // active the app treats logged trades as challenge activity — this matches the
      // Dashboard equity curve, which is date-based. We can't use prop_account_id: it FKs
      // to prop_accounts, not prop_challenges, so it never matches active.id.
      const startDate = active.started_at ? String(active.started_at).slice(0, 10) : null;
      const { data: journalTagged } = startDate
        ? await (supabase as any)
            .from("trades")
            .select("pnl, date, time, created_at")
            .eq("user_id", user!.id)
            .gte("date", startDate)
        : { data: [] };
      const journalClosed = (journalTagged ?? []).map((t: any) => {
        const ts = t.date ? `${t.date}T${t.time || "12:00"}:00` : t.created_at;
        return { pnl: Number(t.pnl ?? 0), status: "closed", closed_at: ts, opened_at: ts };
      });

      const closed: any[] = [...(trades ?? []), ...journalClosed];
      const today = new Date().toISOString().split("T")[0];
      const cumPnl = closed.reduce((s, t) => s + (t.pnl ?? 0), 0);
      const dayPnl = closed
        .filter((t) => ((t.closed_at ?? t.opened_at) ?? "").startsWith(today))
        .reduce((s, t) => s + (t.pnl ?? 0), 0);

      const targetAmt = active.account_size * (active.profit_target / 100);
      const maxLossAmt = active.account_size * (active.max_loss_limit / 100);
      const dailyLossAmt = active.account_size * (active.daily_loss_limit / 100);
      const drawdown = active.starting_balance - (active.starting_balance + cumPnl);

      const violations: any[] = [...(active.violations || [])];
      let newStatus: "passed" | "failed" | null = null;

      if (maxLossAmt > 0 && drawdown > maxLossAmt && !violations.some((v) => v.type === "max_loss")) {
        violations.push({
          type: "max_loss",
          message: `Max drawdown exceeded: -$${drawdown.toFixed(0)} (${active.max_loss_limit}% limit)`,
          timestamp: new Date().toISOString(),
        });
        newStatus = "failed";
      } else if (dailyLossAmt > 0 && dayPnl < -dailyLossAmt && !violations.some((v) => v.type === "daily_loss")) {
        violations.push({
          type: "daily_loss",
          message: `Daily loss limit hit: $${dayPnl.toFixed(0)} (${active.daily_loss_limit}% limit)`,
          timestamp: new Date().toISOString(),
        });
        newStatus = "failed";
      } else if (cumPnl >= targetAmt) {
        newStatus = "passed";
      }

      if (!newStatus) return;

      // Prevent duplicate notifications before the async DB update completes.
      notifiedRef.current = active.id;

      await (supabase as any)
        .from("prop_challenges")
        .update({ status: newStatus, violations, completed_at: new Date().toISOString() })
        .eq("id", active.id);

      if (newStatus === "passed") {
        toast.success(`Challenge passed! +$${cumPnl.toFixed(0)} — profit target reached`, {
          description: "Tap to view your certificate",
          duration: 10_000,
          action: { label: "View", onClick: () => navigate("/prop-challenge") },
        });
      } else {
        toast.error("Challenge failed — loss limit hit", {
          description: "Tap to review your performance",
          duration: 10_000,
          action: { label: "Review", onClick: () => navigate("/prop-challenge") },
        });
      }
    }

    const interval = setInterval(checkChallenge, POLL_MS);
    checkChallenge();

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user?.id, navigate]);

  return null;
}
